import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Play, Pause, Volume2, Loader2, Book } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStorybookBySlug } from '../hooks/usePublishedStorybooks';
import { usePortalSettings } from '../hooks/usePortalSettings';
import { useAuth } from '../../../contexts/AuthContext';
import { getIntroForParent } from '../../../services/portalAdminService';
import { FounderIntro } from '../components/FounderIntro';

export const StorybookIntro: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { storybook, loading, error } = useStorybookBySlug(slug);
  const { settings: portalSettings } = usePortalSettings();
  const { profile } = useAuth();
  const [hasListened, setHasListened] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [personalizedIntroUrl, setPersonalizedIntroUrl] = useState<string | null>(null);
  const [loadingIntro, setLoadingIntro] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Use storybook-specific founder image, or fall back to global settings
  const founderImageUrl = storybook?.founderImageUrl || portalSettings?.founderImageUrl;

  // Fetch personalized intro for current parent
  useEffect(() => {
    const fetchPersonalizedIntro = async () => {
      if (!storybook?.id || !profile?.id) return;
      
      setLoadingIntro(true);
      const introUrl = await getIntroForParent(storybook.id, profile.id);
      setPersonalizedIntroUrl(introUrl);
      setLoadingIntro(false);
    };

    fetchPersonalizedIntro();
  }, [storybook?.id, profile?.id]);

  // Determine which intro audio URL to use (personalized > default)
  const effectiveIntroAudioUrl = personalizedIntroUrl || storybook?.introAudioUrl || null;

  // No auto-play - browsers block it. User must tap to start audio.

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  const handleAudioPlay = () => setIsPlaying(true);
  const handleAudioPause = () => setIsPlaying(false);
  const handleAudioEnded = () => {
    setIsPlaying(false);
    setHasListened(true);
  };

  const handleStartReading = () => {
    navigate(`/storybooks/${slug}/read`);
  };

  if (loading || loadingIntro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading storybook...</p>
        </div>
      </div>
    );
  }

  if (error || !storybook) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Book className="w-8 h-8 text-red-500" />
          </div>
          <p className="text-red-600 mb-4">Storybook not found</p>
          <Link 
            to="/storybooks" 
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Library
          </Link>
        </div>
      </div>
    );
  }

  const hasIntroAudio = !!effectiveIntroAudioUrl;
  const hasFounderImage = !!founderImageUrl;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header - Clean design */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link 
            to="/storybooks"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <Book className="w-5 h-5" />
            <span>Library</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          {/* Cover Image or Founder Intro */}
          {hasFounderImage && hasIntroAudio ? (
            <FounderIntro
              founderImageUrl={founderImageUrl!}
              introAudioUrl={effectiveIntroAudioUrl!}
              title={storybook.title}
              onComplete={() => setHasListened(true)}
            />
          ) : (
            <>
              {/* Cover Image */}
              <div className="w-64 h-64 mx-auto mb-8 rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-blue-100 to-indigo-100">
                {storybook.coverImageUrl ? (
                  <img
                    src={storybook.coverImageUrl}
                    alt={storybook.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Book className="w-24 h-24 text-blue-300" />
                  </div>
                )}
              </div>

              {/* Title */}
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {storybook.title}
              </h1>

              {/* Description */}
              {storybook.description && (
                <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                  {storybook.description}
                </p>
              )}

              {/* Simple Audio Player (if no founder image but has audio) */}
              {hasIntroAudio && !hasFounderImage && (
                <div className="mb-8">
                  <audio
                    ref={audioRef}
                    src={effectiveIntroAudioUrl!}
                    onPlay={handleAudioPlay}
                    onPause={handleAudioPause}
                    onEnded={handleAudioEnded}
                  />
                  <button
                    onClick={handlePlayPause}
                    className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-md hover:shadow-lg transition-all border border-gray-200"
                  >
                    {isPlaying ? (
                      <>
                        <Pause className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-gray-700">Pause Introduction</span>
                        <Volume2 className="w-5 h-5 text-blue-500 animate-pulse" />
                      </>
                    ) : (
                      <>
                        <Play className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-gray-700">
                          {hasListened ? 'Replay Introduction' : 'Listen to Introduction'}
                        </span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Start Reading Button - with spacing from replay button */}
          <AnimatePresence>
            {(!hasIntroAudio || hasListened || !hasFounderImage) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="mt-4"
              >
                <button
                  onClick={handleStartReading}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-indigo-700 transition-all min-w-[200px]"
                >
                  <Book className="w-6 h-6" />
                  Start Reading
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Skip Button (shown during audio playback) */}
          {hasIntroAudio && hasFounderImage && !hasListened && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2 }}
              onClick={handleStartReading}
              className="mt-6 text-gray-500 hover:text-gray-700 text-sm underline"
            >
              Skip introduction
            </motion.button>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default StorybookIntro;
